import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
  Alert,
  TableContainer,
  Paper,
  IconButton,
  Button,
  Link,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Router as OnuIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  DeleteOutline as DesprovisionIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Link as RouterLink } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../../services/useApi';
import OnusModal from './OnusModal';
import '../../../styles/swal-overrides.css';

interface ONU {
  id: number;
  onu_id: number;
  serialNumber: string;
  description?: string;
  descricao?: string;
  portId?: number;
  portNumber?: string;
  slot?: number;
  sfp?: string;
  cardId?: number;
  services_port?: number | string;
  txpower?: number;
  rxpower?: number;
  status?: string;
  distance?: number;
  ipv4_address?: string;
  cliente?: { cliente_id: number; nome: string; tituloContrato: string };
  equipamento?: { id: number; marca: string; modelo: string; gpon_sn: string | null };
}

interface OnusTabProps {
  oltId?: string;
}

const OnusTab: React.FC<OnusTabProps> = ({ oltId }) => {
  const [onus, setOnus] = useState<ONU[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [filterSerial, setFilterSerial] = useState('');
  const [filterCliente, setFilterCliente] = useState('');
  const [filterEquipamento, setFilterEquipamento] = useState('');
  const [filterDescricao, setFilterDescricao] = useState('');
  const [filterPorta, setFilterPorta] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [filterSinal, setFilterSinal] = useState<string>('todos');
  const [sortBy, setSortBy] = useState<string>('porta');
  const [desprovisioningId, setDesprovisioningId] = useState<number | null>(null);
  const [getInfoLoadingId, setGetInfoLoadingId] = useState<number | null>(null);
  const [onusModalOpen, setOnusModalOpen] = useState(false);
  const [selectedOnuForPort, setSelectedOnuForPort] = useState<ONU | null>(null);

  const fetchAllOnus = async () => {
    if (!oltId) return;

    setLoading(true);
    try {
      const response = await api.get(`/olts/${oltId}/onus`);
      const data = response.data?.data ?? response.data;
      const list = Array.isArray(data) ? data : [];
      setOnus(list);
    } catch (err: any) {
      console.error('Erro ao carregar ONUs:', err);
      toast.error(err.response?.data?.message || 'Erro ao carregar ONUs');
      setOnus([]);
    } finally {
      setLoading(false);
    }
  };

  const syncAllOnus = async () => {
    if (!oltId) return;

    setSyncLoading(true);
    try {
      const cardsResponse = await api.get(`/olts/${oltId}/cards`);
      if (!cardsResponse.data?.success && !Array.isArray(cardsResponse.data?.data)) {
        toast.error('Erro ao carregar cards da OLT');
        return;
      }

      const cards = cardsResponse.data?.data ?? [];
      let totalCreated = 0;
      let totalUpdated = 0;

      for (const card of cards) {
        const portsResponse = await api.get(`/olts/${oltId}/cards/${card.slot}/ports`);
        if (!portsResponse.data?.success) continue;

        const ports = portsResponse.data?.data ?? [];
        for (const port of ports) {
          try {
            const syncResponse = await api.post(
              `/olts/onus/slots/${card.id}/ports/${port.id}/sync`
            );
            if (syncResponse.data?.success && syncResponse.data?.data) {
              totalCreated += syncResponse.data.data.onusCreated ?? 0;
              totalUpdated += syncResponse.data.data.onusUpdated ?? 0;
            }
          } catch {
            // ignora porta sem ONUs ou erro de sync
          }
        }
      }

      toast.success(
        `Sincronização concluída: ${totalCreated} ONUs criadas, ${totalUpdated} ONUs atualizadas`
      );
      await fetchAllOnus();
    } catch (err: any) {
      console.error('Erro ao sincronizar ONUs:', err);
      toast.error('Erro ao sincronizar ONUs');
    } finally {
      setSyncLoading(false);
    }
  };

  useEffect(() => {
    if (oltId) fetchAllOnus();
  }, [oltId]);

  const handleGetInfo = async (onu: ONU) => {
    setGetInfoLoadingId(onu.id);
    try {
      const response = await api.post('/olts/onus/get-info', { id: onu.id }) as any;
      const debugResult = response.data?.debug_result ?? response.data?.details ?? 'Sem dados retornados';
      const data = response.data?.data;
      let html = `<pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto; max-height: 500px; white-space: pre-wrap;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`;
      if (data && Object.keys(data).length > 0) {
        const dataStr = JSON.stringify(data, null, 2);
        html = `<pre style="text-align: left; background: #1e3a5f; color: #e0e0e0; padding: 10px; border-radius: 5px; font-size: 11px; margin-bottom: 10px;">${dataStr.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>${html}`;
      }
      await Swal.fire({
        title: response.data?.success ? '📈 Informações da ONU' : 'Erro ao obter informações',
        html,
        width: '700px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } catch (err: any) {
      const errorData = err.response?.data;
      const debugResult = errorData?.debug_result ?? errorData?.error ?? err.message ?? 'Erro desconhecido';
      await Swal.fire({
        title: 'Erro ao obter informações',
        html: `<p style="text-align: left; margin-bottom: 10px;">${errorData?.message || 'Falha ao consultar a OLT.'}</p>
               <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${String(debugResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>`,
        icon: 'error',
        width: '600px',
        customClass: { container: 'swal-zindex swal-onu-info', popup: 'swal-onu-info' }
      });
    } finally {
      setGetInfoLoadingId(null);
    }
  };

  const handleDesprovisionar = async (onu: ONU) => {
    if (!window.confirm(`Desprovisionar ONU ${onu.onu_id} (${onu.serialNumber}) da porta ${formatPorta(onu)}?`)) return;
    setDesprovisioningId(onu.id);
    try {
      const response = await api.post(`/olts/onus/desprovisionar`, { id: onu.id }) as any;
      if (response.data?.success) {
        toast.success('ONU desprovisionada com sucesso');
        await fetchAllOnus();
      } else {
        toast.error(response.data?.message || 'Erro ao desprovisionar ONU');
      }
    } catch (err: any) {
      console.error('❌ Erro ao desprovisionar ONU:', err);
      const errorData = err.response?.data;

      if (errorData?.debug_result) {
        await Swal.fire({
          title: 'Erro de Desprovisionamento',
          html: `<p style="text-align: left; margin-bottom: 10px;">${errorData.message || 'Falha ao desprovisionar na OLT.'}</p>
                 <pre style="text-align: left; background: #2b2b2b; color: #fff; padding: 10px; border-radius: 5px; font-size: 12px; overflow-x: auto;">${errorData.debug_result}</pre>`,
          icon: 'error',
          width: '600px',
          customClass: {
            container: 'swal-zindex'
          }
        });
      } else {
        toast.error(errorData?.message || 'Erro ao desprovisionar ONU');
      }
    } finally {
      setDesprovisioningId(null);
    }
  };

  const formatPorta = (onu: ONU) => {
    if (onu.sfp) return onu.sfp;
    if (onu.slot != null && onu.portNumber != null) return `${onu.slot}/${onu.portNumber}`;
    return '—';
  };

  const portOptions = useMemo(() => {
    const labels = onus.map(formatPorta).filter((p) => p !== '—');
    return Array.from(new Set(labels)).sort();
  }, [onus]);

  const filteredOnus = useMemo(() => {
    const serial = filterSerial.trim().toLowerCase();
    const cliente = filterCliente.trim().toLowerCase();
    const equipamento = filterEquipamento.trim().toLowerCase();
    const descricao = filterDescricao.trim().toLowerCase();
    const porta = filterPorta.trim();
    const status = filterStatus;

    let filtered = onus.filter((onu) => {
      if (serial && !(onu.serialNumber ?? '').toLowerCase().includes(serial)) return false;
      if (cliente && !(onu.cliente?.nome ?? '').toLowerCase().includes(cliente)) return false;
      const eqLabel = onu.equipamento
        ? `${onu.equipamento.marca} ${onu.equipamento.modelo} ${onu.equipamento.gpon_sn ?? ''}`.toLowerCase()
        : '';
      if (equipamento && !eqLabel.includes(equipamento)) return false;
      const desc = (onu.descricao || onu.description || '').toLowerCase();
      if (descricao && !desc.includes(descricao)) return false;
      if (porta && formatPorta(onu) !== porta) return false;
      if (status !== 'todos') {
        const onuStatus = onu.status?.toLowerCase() || 'offline';
        if (onuStatus !== status) return false;
      }
      if (filterSinal !== 'todos') {
        if (onu.rxpower == null) return false;
        if (filterSinal === 'bom') return onu.rxpower > -23;
        if (filterSinal === 'razoavel') return onu.rxpower >= -27 && onu.rxpower <= -23;
        if (filterSinal === 'fraco') return onu.rxpower < -27;
      }
      return true;
    });

    // Aplica a ordenação
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'serial':
          return (a.serialNumber || '').localeCompare(b.serialNumber || '');
        case 'porta':
          return formatPorta(a).localeCompare(formatPorta(b));
        case 'rxpower':
          return (a.rxpower || -100) - (b.rxpower || -100);
        case 'txpower':
          return (a.txpower || -100) - (b.txpower || -100);
        case 'distance':
          return (a.distance || 0) - (b.distance || 0);
        case 'status':
          return (a.status || 'offline').localeCompare(b.status || 'offline');
        case 'onu_id':
        default:
          return (a.onu_id || 0) - (b.onu_id || 0);
      }
    });

    return filtered;
  }, [onus, filterSerial, filterCliente, filterEquipamento, filterDescricao, filterPorta, filterStatus, filterSinal, sortBy]);

  const getSignalStrengthColor = (signal: number) => {
    if (signal > -23) return 'success';   // Bom
    if (signal >= -27) return 'warning';  // Razoável
    return 'error';                       // Fraco
  };

  if (!oltId) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          ONUs cadastradas
        </Typography>
        <Alert severity="warning">Selecione uma OLT para visualizar as ONUs.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <OnuIcon color="primary" />
          <Typography variant="h6">ONUs cadastradas na OLT</Typography>
          {onus.length > 0 && (
            <Chip
              label={
                filteredOnus.length !== onus.length
                  ? `${filteredOnus.length} de ${onus.length} ONUs`
                  : `${onus.length} ONUs`
              }
              color="primary"
              size="small"
            />
          )}
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={syncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={syncAllOnus}
            disabled={syncLoading || loading}
            size="small"
          >
            {syncLoading ? 'Sincronizando...' : 'Sincronizar ONUs'}
          </Button>
          <IconButton
            onClick={fetchAllOnus}
            disabled={loading || syncLoading}
            color="primary"
            size="small"
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {onus.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <FilterIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" color="text.secondary">
              Filtros
            </Typography>
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="flex-start"
          >
            <TextField
              size="small"
              label="Serial (GPON)"
              value={filterSerial}
              onChange={(e) => setFilterSerial(e.target.value)}
              placeholder="Buscar por serial..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Cliente"
              value={filterCliente}
              onChange={(e) => setFilterCliente(e.target.value)}
              placeholder="Buscar por cliente..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Equipamento"
              value={filterEquipamento}
              onChange={(e) => setFilterEquipamento(e.target.value)}
              placeholder="Marca, modelo ou serial..."
              sx={{ minWidth: 180 }}
            />
            <TextField
              size="small"
              label="Descrição"
              value={filterDescricao}
              onChange={(e) => setFilterDescricao(e.target.value)}
              placeholder="Buscar na descrição..."
              sx={{ minWidth: 180 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-porta-label">Porta</InputLabel>
              <Select
                labelId="filter-porta-label"
                value={filterPorta}
                label="Porta"
                onChange={(e) => setFilterPorta(e.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                {portOptions.map((porta) => (
                  <MenuItem key={porta} value={porta}>
                    {porta}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select
                labelId="filter-status-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="down">Down</MenuItem>
                <MenuItem value="offline">Offline</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="filter-sinal-label">Sinal</InputLabel>
              <Select
                labelId="filter-sinal-label"
                value={filterSinal}
                label="Sinal"
                onChange={(e) => setFilterSinal(e.target.value)}
              >
                <MenuItem value="todos">Todos</MenuItem>
                <MenuItem value="bom">Bom &lt; -23</MenuItem>
                <MenuItem value="razoavel">Razoável -23 a -27</MenuItem>
                <MenuItem value="fraco">Fraco -27 &gt;</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sort-by-label">Ordenar por</InputLabel>
              <Select
                labelId="sort-by-label"
                value={sortBy}
                label="Ordenar por"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="porta">Porta (Padrão)</MenuItem>
                <MenuItem value="onu_id">ONU ID</MenuItem>
                <MenuItem value="serial">Serial Number</MenuItem>
                <MenuItem value="rxpower">Sinal (Rx Power)</MenuItem>
                <MenuItem value="txpower">Sinal (Tx Power)</MenuItem>
                <MenuItem value="distance">Distância</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : onus.length > 0 ? (
        <>
          {filteredOnus.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nenhuma ONU corresponde aos filtros. Ajuste os critérios ou limpe os filtros.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><strong>ONU ID</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Serial (GPON)</strong></TableCell>
                    <TableCell><strong>Porta</strong></TableCell>
                    <TableCell><strong>IP/Rede</strong></TableCell>
                    <TableCell><strong>Sinal (Rx/Tx)</strong></TableCell>
                    <TableCell><strong>Distância</strong></TableCell>
                    <TableCell><strong>Descrição</strong></TableCell>
                    <TableCell><strong>Service Port</strong></TableCell>
                    <TableCell><strong>Cliente</strong></TableCell>
                    <TableCell><strong>Equipamento</strong></TableCell>
                    <TableCell align="center"><strong>Ação</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredOnus.map((onu) => (
                    <TableRow key={onu.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {onu.onu_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={onu.status === 'online' ? 'Online' : onu.status === 'down' ? 'Down' : onu.status || 'Offline'}
                          size="small"
                          color={onu.status === 'online' ? 'success' : onu.status === 'down' ? 'error' : 'default'}
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {onu.serialNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatPorta(onu)}
                          color="primary"
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedOnuForPort(onu);
                            setOnusModalOpen(true);
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.ipv4_address || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: '0.8rem',
                            color: onu.rxpower != null ? `${getSignalStrengthColor(onu.rxpower)}.main` : 'text.secondary'
                          }}
                        >
                          {onu.rxpower != null ? `${onu.rxpower} dBm` : '—'} / {onu.txpower != null ? `${onu.txpower} dBm` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          {onu.distance != null ? `${onu.distance} m` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {onu.descricao || onu.description || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                          [{onu.services_port != null ? String(onu.services_port).replace(/,/g, ', ') : '—'}]
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {onu.cliente ? (
                          <Typography variant="body2">{onu.cliente.nome}</Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {onu.equipamento ? (
                          <Link
                            component={RouterLink}
                            to={`/equipments/${onu.equipamento.id}`}
                            variant="body2"
                          >
                            {onu.equipamento.marca} {onu.equipamento.modelo}
                          </Link>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          color="info"
                          size="small"
                          aria-label="Obter informações"
                          onClick={() => handleGetInfo(onu)}
                          disabled={getInfoLoadingId !== null}
                          sx={{ fontSize: '0.75rem', mr: 0.5 }}
                        >
                          {getInfoLoadingId === onu.id ? <CircularProgress size={18} /> : <InfoIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          aria-label="Desprovisionar"
                          onClick={() => handleDesprovisionar(onu)}
                          disabled={desprovisioningId !== null}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {desprovisioningId === onu.id ? <CircularProgress size={18} /> : <DesprovisionIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
          <OnuIcon color="action" sx={{ fontSize: 64 }} />
          <Typography variant="h6" color="text.secondary">
            Nenhuma ONU cadastrada nesta OLT
          </Typography>
          <Typography variant="body2" color="text.secondary">
            As ONUs aparecem aqui após serem sincronizadas por porta. Use &quot;Sincronizar ONUs&quot; nas portas ou o botão abaixo.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SyncIcon />}
            onClick={syncAllOnus}
            disabled={syncLoading}
          >
            Sincronizar ONUs
          </Button>
        </Box>
      )}

      <OnusModal
        open={onusModalOpen}
        onClose={() => {
          setOnusModalOpen(false);
          setSelectedOnuForPort(null);
        }}
        oltId={oltId}
        cardSlot={selectedOnuForPort?.slot}
        portNumber={selectedOnuForPort?.portNumber}
        portId={selectedOnuForPort?.portId}
        cardId={selectedOnuForPort?.cardId}
      />
    </Box>
  );
};

export default OnusTab;
